import React, { useState, useEffect, useRef } from 'react';
import { getSettings, updateSettings, testSMTP } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Save, Send, Loader2, Mail, Upload, Trash2, Building2, Package, FileText, Truck, Plus, X, Key } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import TranslationSettings from '../components/TranslationSettings';

export default function Settings() {
  const { user } = useAuth();
  const isOperator = user?.role === 'operator';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const fileInputRef = useRef(null);
  const [newTransportOption, setNewTransportOption] = useState('');
  
  // Password change state for operators
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };
  
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_encryption: 'tls',
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_reply_to: '',
    dispatch_number_prefix: '',
    data_retention_years: 7,
    audit_log_retention_days: 30,
    company_name: 'Cashel Blue',
    company_logo: null,
    company_address: '',
    company_phone: '',
    company_email: '',
    product_formats: [],
    transport_options: ['CMT', 'Dawn', 'AOR', 'Collection', 'DPD/Courier', 'Other'],
    auto_qc_approve_batches: true,
    case_tare_weight: 0.19,
    dispatch_email_subject: 'Dispatch Note {dispatch_number} - {company_name}',
    dispatch_email_body: `Dear {customer_name},

Please find attached the dispatch note {dispatch_number} for your order.

Order Details:
- PO Number: {po_number}
- Dispatch Date: {dispatch_date}

If you have any questions, please don't hesitate to contact us.

Best regards,
{company_name}`,
    recall_email_subject: 'URGENT: Product Recall Notice - {batch_code} - {company_name}',
    recall_email_body: `URGENT PRODUCT RECALL NOTICE

Dear {customer_name},

We are writing to inform you of a product recall that may affect items you have purchased from us.

RECALL DETAILS:
- Batch Code: {batch_code}
- Product: {product_name}
- Use By Date: {use_by_date}
- Your Invoice: {invoice_number}
- Quantity Affected: {quantity} kg

REASON FOR RECALL:
{recall_reason}

REQUIRED ACTION:
Please immediately quarantine any affected stock and do not sell or distribute these products. Contact us as soon as possible to arrange collection or disposal.

We sincerely apologize for any inconvenience this may cause and appreciate your cooperation in ensuring food safety.

If you have any questions, please contact us immediately.

Best regards,
{company_name}
{company_phone}
{company_email}`,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanedSettings = {
        ...settings,
        product_formats: (settings.product_formats || []).map(f => f.trim()).filter(f => f),
        transport_options: (settings.transport_options || []).map(f => f.trim()).filter(f => f),
      };
      await updateSettings(cleanedSettings);
      setSettings(cleanedSettings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMTP = async () => {
    if (!testEmail) {
      toast.error('Enter an email address to test');
      return;
    }

    setTesting(true);
    try {
      await testSMTP(testEmail);
      toast.success('Test email sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setSettings(prev => ({ ...prev, company_logo: response.data.logo }));
      toast.success('Logo uploaded and resized successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await api.delete('/settings/logo');
      setSettings(prev => ({ ...prev, company_logo: null }));
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // For operators, show only password change section
  if (isOperator) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="settings-page">
        <TranslationSettings isAdmin={false} />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your account password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                placeholder="Enter current password"
                data-testid="input-current-password"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="Enter new password (min 8 characters)"
                data-testid="input-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                data-testid="input-confirm-password"
              />
            </div>
            <Button
              onClick={handlePasswordChange}
              disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword}
              data-testid="btn-change-password"
            >
              {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Name:</strong> {user?.name}</p>
            <p><strong>Email:</strong> {user?.email}</p>
            <p><strong>Role:</strong> Operator</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="settings-page">
      {/* Language & Translations, visible for all admins */}
      <TranslationSettings isAdmin={['super_admin', 'admin'].includes(user?.role)} />

      {/* Password Change for Admins */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>
            Update your account password
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                placeholder="Current password"
                data-testid="input-current-password"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="New password"
                data-testid="input-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Confirm"
                data-testid="input-confirm-password"
              />
            </div>
          </div>
          <Button
            onClick={handlePasswordChange}
            disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword}
            size="sm"
            data-testid="btn-change-password"
          >
            {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
            Change Password
          </Button>
        </CardContent>
      </Card>

      {/* Company Branding */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Company Branding
          </CardTitle>
          <CardDescription>
            Customize your company logo and name
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Company Name</Label>
            <Input
              value={settings.company_name}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              placeholder="Cashel Blue"
              data-testid="input-company-name"
            />
          </div>

          <div className="space-y-2">
            <Label>Company Logo</Label>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              {settings.company_logo ? (
                <div className="relative">
                  <img 
                    src={settings.company_logo} 
                    alt="Company Logo" 
                    className="h-20 w-auto max-w-[200px] object-contain border rounded-lg p-2 bg-white"
                    data-testid="company-logo-preview"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                    onClick={handleRemoveLogo}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="h-20 w-40 border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground text-sm">
                  No logo uploaded
                </div>
              )}
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="btn-upload-logo"
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {settings.company_logo ? 'Change Logo' : 'Upload Logo'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG up to 10MB. Auto-resized to max 400×200px
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Company Address</Label>
            <Input
              value={settings.company_address}
              onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
              placeholder="e.g., Beechmount, Fethard, Co. Tipperary, Ireland"
              data-testid="input-company-address"
            />
            <p className="text-xs text-muted-foreground">
              Full postal address for dispatch notes and invoices
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company Phone</Label>
              <Input
                value={settings.company_phone}
                onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                placeholder="e.g., +353 52 613 1151"
                data-testid="input-company-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Company Email</Label>
              <Input
                type="email"
                value={settings.company_email}
                onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                placeholder="e.g., info@cashelblue.ie"
                data-testid="input-company-email"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Product Formats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Product Formats
          </CardTitle>
          <CardDescription>
            Define the available product formats/sizes for orders and dispatches (e.g., 50g, 100g, 150g)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Available Formats (one per line)</Label>
            <textarea
              className="w-full min-h-[150px] p-3 border rounded-md font-mono text-sm bg-background"
              value={(settings.product_formats || []).join('\n')}
              onChange={(e) => setSettings({ 
                ...settings, 
                product_formats: e.target.value.split('\n')
              })}
              placeholder="50g
100g
150g
200g
250g
1kg
Whole Wheel
Half Wheel"
              data-testid="input-product-formats"
            />
            <p className="text-sm text-muted-foreground">
              Enter each format on a new line. These will appear as dropdown options in Sales Order and Dispatch screens.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Transport Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Transport Options
          </CardTitle>
          <CardDescription>
            Define the available transport/carrier options for orders (e.g., CMT, Dawn, Collection)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Available Transport Options (one per line)</Label>
            <textarea
              className="w-full min-h-[120px] p-3 border rounded-md font-mono text-sm bg-background"
              value={(settings.transport_options || []).join('\n')}
              onChange={(e) => setSettings({ 
                ...settings, 
                transport_options: e.target.value.split('\n')
              })}
              placeholder="CMT
Dawn
AOR
Collection
DPD/Courier
Other"
              data-testid="input-transport-options"
            />
            <p className="text-sm text-muted-foreground">
              Enter each transport option on a new line. These will appear as dropdown options in Sales Order Entry.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Batch Management Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Batch Management
          </CardTitle>
          <CardDescription>
            Configure how batches are handled for QC and dispatch readiness
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
            <div className="space-y-1">
              <Label className="text-base font-medium">Auto-Approve Batches on Creation</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, new batches are automatically QC approved and ready for dispatch. 
                When disabled, batches require manual QC release before they can be dispatched.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.auto_qc_approve_batches ?? true}
                onChange={(e) => setSettings({ ...settings, auto_qc_approve_batches: e.target.checked })}
                className="sr-only peer"
                data-testid="toggle-auto-qc-approve"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            Note: Batches can still be manually put on hold or rejected after creation regardless of this setting.
          </p>
        </CardContent>
      </Card>

      {/* SMTP Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            SMTP Email Settings
          </CardTitle>
          <CardDescription>
            Configure email delivery for password resets and notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SMTP Host</Label>
              <Input
                value={settings.smtp_host}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                placeholder="smtp.example.com"
                data-testid="input-smtp-host"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={settings.smtp_port}
                  onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                  data-testid="input-smtp-port"
                />
              </div>
              <div className="space-y-2">
                <Label>Encryption</Label>
                <Select
                  value={settings.smtp_encryption}
                  onValueChange={(v) => setSettings({ ...settings, smtp_encryption: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="tls">TLS</SelectItem>
                    <SelectItem value="ssl">SSL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input
                value={settings.smtp_username}
                onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                placeholder="your-smtp-username"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={settings.smtp_password}
                onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>From Email</Label>
              <Input
                type="email"
                value={settings.smtp_from_email}
                onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                placeholder="noreply@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>From Name</Label>
              <Input
                value={settings.smtp_from_name}
                onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                placeholder="Cashel Blue Operations"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Reply-To Email</Label>
            <Input
              type="email"
              value={settings.smtp_reply_to}
              onChange={(e) => setSettings({ ...settings, smtp_reply_to: e.target.value })}
              placeholder="support@example.com"
            />
          </div>

          <div className="border-t pt-4">
            <Label className="mb-2 block">Test SMTP Configuration</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="sm:max-w-xs"
              />
              <Button variant="outline" onClick={handleTestSMTP} disabled={testing}>
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Test
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Templates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Email Templates
          </CardTitle>
          <CardDescription>
            Customize the email templates used when sending dispatch notes to customers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Dispatch Email Subject</Label>
            <Input
              value={settings.dispatch_email_subject || ''}
              onChange={(e) => setSettings({ ...settings, dispatch_email_subject: e.target.value })}
              placeholder="Dispatch Note {dispatch_number} - {company_name}"
              data-testid="input-email-subject"
            />
            <p className="text-xs text-muted-foreground">
              Available placeholders: {'{customer_name}'}, {'{dispatch_number}'}, {'{po_number}'}, {'{dispatch_date}'}, {'{company_name}'}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Dispatch Email Body</Label>
            <Textarea
              value={settings.dispatch_email_body || ''}
              onChange={(e) => setSettings({ ...settings, dispatch_email_body: e.target.value })}
              placeholder="Dear {customer_name},..."
              className="min-h-[200px] font-mono text-sm"
              data-testid="input-email-body"
            />
            <p className="text-xs text-muted-foreground">
              This template will be used when emailing dispatch notes. Use the placeholders above to insert dynamic content.
            </p>
          </div>

          <div className="p-4 border rounded-lg bg-muted/30">
            <Label className="text-sm font-medium">Preview</Label>
            <div className="mt-2 p-3 bg-white rounded border text-sm whitespace-pre-wrap">
              <strong>Subject:</strong> {(settings.dispatch_email_subject || 'Dispatch Note {dispatch_number} - {company_name}')
                .replace('{customer_name}', 'John Smith')
                .replace('{dispatch_number}', '2025010001')
                .replace('{po_number}', 'PO-12345')
                .replace('{dispatch_date}', '20/01/2025')
                .replace('{company_name}', settings.company_name || 'Company')
              }
              <hr className="my-2" />
              {(settings.dispatch_email_body || 'Dear {customer_name},...')
                .replace('{customer_name}', 'John Smith')
                .replace('{dispatch_number}', '2025010001')
                .replace('{po_number}', 'PO-12345')
                .replace('{dispatch_date}', '20/01/2025')
                .replace('{company_name}', settings.company_name || 'Company')
              }
            </div>
          </div>

          {/* Recall Email Template */}
          <div className="mt-8 pt-6 border-t">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                <Mail className="w-4 h-4 text-red-600" />
              </span>
              <h3 className="font-semibold text-red-800">Recall Email Template</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              This template is used when sending recall notifications to affected customers.
            </p>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Recall Email Subject</Label>
                <Input
                  value={settings.recall_email_subject || ''}
                  onChange={(e) => setSettings({ ...settings, recall_email_subject: e.target.value })}
                  placeholder="URGENT: Product Recall Notice - {batch_code}"
                  className="border-red-200 focus:border-red-400"
                  data-testid="input-recall-email-subject"
                />
                <p className="text-xs text-muted-foreground">
                  Available placeholders: {'{customer_name}'}, {'{batch_code}'}, {'{product_name}'}, {'{use_by_date}'}, {'{invoice_number}'}, {'{quantity}'}, {'{company_name}'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Recall Email Body</Label>
                <Textarea
                  value={settings.recall_email_body || ''}
                  onChange={(e) => setSettings({ ...settings, recall_email_body: e.target.value })}
                  placeholder="URGENT PRODUCT RECALL NOTICE..."
                  className="min-h-[300px] font-mono text-sm border-red-200 focus:border-red-400"
                  data-testid="input-recall-email-body"
                />
                <p className="text-xs text-muted-foreground">
                  Additional placeholders: {'{recall_reason}'}, {'{company_phone}'}, {'{company_email}'}
                </p>
              </div>

              <div className="p-4 border border-red-200 rounded-lg bg-red-50/50">
                <Label className="text-sm font-medium text-red-800">Preview</Label>
                <div className="mt-2 p-3 bg-white rounded border border-red-100 text-sm whitespace-pre-wrap">
                  <strong>Subject:</strong> {(settings.recall_email_subject || 'URGENT: Product Recall Notice - {batch_code}')
                    .replace('{customer_name}', 'ABC Foods Ltd')
                    .replace('{batch_code}', 'BATCH-2025-001')
                    .replace('{product_name}', 'Cashel Blue 350g')
                    .replace('{use_by_date}', '30/06/2025')
                    .replace('{invoice_number}', 'SI-25001')
                    .replace('{quantity}', '50.00')
                    .replace('{company_name}', settings.company_name || 'Company')
                  }
                  <hr className="my-2" />
                  {(settings.recall_email_body || 'URGENT PRODUCT RECALL NOTICE...')
                    .replace('{customer_name}', 'ABC Foods Ltd')
                    .replace('{batch_code}', 'BATCH-2025-001')
                    .replace('{product_name}', 'Cashel Blue 350g')
                    .replace('{use_by_date}', '30/06/2025')
                    .replace('{invoice_number}', 'SI-25001')
                    .replace('{quantity}', '50.00')
                    .replace('{recall_reason}', '[Recall reason will be entered when sending]')
                    .replace('{company_name}', settings.company_name || 'Company')
                    .replace('{company_phone}', settings.company_phone || '')
                    .replace('{company_email}', settings.company_email || '')
                  }
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Other Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Other Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Dispatch Number Prefix</Label>
              <Input
                value={settings.dispatch_number_prefix}
                onChange={(e) => setSettings({ ...settings, dispatch_number_prefix: e.target.value })}
                placeholder="Optional prefix"
              />
            </div>
            <div className="space-y-2">
              <Label>Case Tare Weight (kg)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="2"
                value={settings.case_tare_weight || 0.19}
                onChange={(e) => setSettings({ ...settings, case_tare_weight: parseFloat(e.target.value) || 0.19 })}
                data-testid="input-case-tare-weight"
              />
              <p className="text-xs text-muted-foreground">
                Weight added per case for packaging/tare. Default: 0.19 kg.
                <br />
                <span className="font-medium">Gross Weight Formula:</span> Net Weight + (Cases × {settings.case_tare_weight || 0.19} kg)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Data Retention (Years)</Label>
              <Input
                type="number"
                value={settings.data_retention_years}
                onChange={(e) => setSettings({ ...settings, data_retention_years: parseInt(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground">How long to keep dispatch and batch records</p>
            </div>
            <div className="space-y-2">
              <Label>Audit Log Retention (Days)</Label>
              <Input
                type="number"
                min="7"
                max="365"
                value={settings.audit_log_retention_days}
                onChange={(e) => setSettings({ ...settings, audit_log_retention_days: parseInt(e.target.value) || 30 })}
              />
              <p className="text-xs text-muted-foreground">Minimum 7 days, maximum 365 days. Logs older than this can be cleaned up from Reports → Audit.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pb-6">
        <Button onClick={handleSave} disabled={saving} data-testid="btn-save-settings">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      {/* Geo-Restriction Section, only visible to super_admin */}
      {user?.role === 'super_admin' && (
        <GeoRestrictionSection settings={settings} setSettings={setSettings} onSave={handleSave} saving={saving} />
      )}
    </div>
  );
}


/**
 * GeoRestrictionSection, country-based login restriction UI.
 * Allows super_admin to enable/disable geo-restriction and manage the
 * allowlist of ISO 3166-1 alpha-2 country codes.
 */
function GeoRestrictionSection({ settings, setSettings, onSave, saving }) {
  const [newCountry, setNewCountry] = React.useState('');

  const enabled = settings.geo_restriction_enabled || false;
  const countries = settings.geo_allowed_countries || [];

  const COMMON_COUNTRIES = [
    { code: 'IE', name: 'Ireland' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'BE', name: 'Belgium' },
    { code: 'DK', name: 'Denmark' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'AT', name: 'Austria' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'AU', name: 'Australia' },
    { code: 'CA', name: 'Canada' },
    { code: 'JP', name: 'Japan' },
    { code: 'IN', name: 'India' },
  ];

  const addCountry = (code) => {
    if (code && !countries.includes(code.toUpperCase())) {
      setSettings(prev => ({
        ...prev,
        geo_allowed_countries: [...(prev.geo_allowed_countries || []), code.toUpperCase()],
      }));
      setNewCountry('');
    }
  };

  const removeCountry = (code) => {
    setSettings(prev => ({
      ...prev,
      geo_allowed_countries: (prev.geo_allowed_countries || []).filter(c => c !== code),
    }));
  };

  return (
    <Card className="border-amber-200" data-testid="geo-restriction-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Key className="w-4 h-4" />
          Geo-Restriction (Country-Based Login Control)
        </CardTitle>
        <CardDescription>
          Restrict logins to users from specific countries. Uses IP geolocation. Blocked attempts are logged to Compliance Center.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium">Enable Geo-Restriction</label>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setSettings(prev => ({ ...prev, geo_restriction_enabled: e.target.checked }))}
            className="h-4 w-4"
            data-testid="geo-restriction-toggle"
          />
          <span className="text-xs text-muted-foreground">{enabled ? 'Enabled, only listed countries can log in' : 'Disabled, all countries allowed'}</span>
        </div>

        {enabled && (
          <>
            <div>
              <Label className="text-sm">Allowed Countries</Label>
              <div className="flex flex-wrap gap-2 mt-2 mb-3">
                {countries.map(code => {
                  const found = COMMON_COUNTRIES.find(c => c.code === code);
                  return (
                    <span key={code} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-800 text-xs font-medium border border-emerald-200">
                      {found ? `${found.name} (${code})` : code}
                      <button onClick={() => removeCountry(code)} className="hover:text-red-600 ml-1" data-testid={`remove-country-${code}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
                {countries.length === 0 && (
                  <span className="text-xs text-amber-600">No countries added, restriction is effectively disabled until you add at least one.</span>
                )}
              </div>
              <div className="flex gap-2">
                <Select value={newCountry} onValueChange={(v) => addCountry(v)}>
                  <SelectTrigger className="w-[200px]" data-testid="add-country-select">
                    <SelectValue placeholder="Add country..." />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_COUNTRIES.filter(c => !countries.includes(c.code)).map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Or type ISO code (e.g. IE)"
                  value={newCountry}
                  onChange={(e) => setNewCountry(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter' && newCountry.length === 2) addCountry(newCountry); }}
                  className="w-[160px]"
                  maxLength={2}
                  data-testid="add-country-input"
                />
                <Button variant="outline" size="sm" onClick={() => addCountry(newCountry)} disabled={newCountry.length !== 2} data-testid="add-country-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={onSave} disabled={saving} data-testid="save-geo-settings">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Geo Settings
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
